/**
 * @fileoverview Sentry configuration for frontend error tracking and performance monitoring
 *
 * This module initializes Sentry for the React application with environment-aware configuration.
 * It skips initialization in local development and provides comprehensive error tracking,
 * performance monitoring, and user session replay for production environments.
 */

import * as Sentry from '@sentry/react';
import { createBrowserRouter } from 'react-router-dom';
import { initializeSentryWithDebug, logSentryDebugInfo } from './sentryDebug';

/**
 * Sentry configuration interface for type safety
 */
interface SentryConfig {
	dsn: string;
	environment: string;
	tracesSampleRate: number;
	replaysSessionSampleRate: number;
	replaysOnErrorSampleRate: number;
	integrations: Sentry.Integration[];
}

/**
 * Determines if Sentry should be initialized based on environment
 *
 * @returns {boolean} True if Sentry should be initialized, false for local development
 */
function shouldInitializeSentry(): boolean {
	const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT;
	const dsn = import.meta.env.VITE_SENTRY_DSN;

	// Debug logging
	console.log('🔍 Sentry Debug - Frontend Environment Check:');
	console.log('  VITE_SENTRY_ENVIRONMENT:', environment);
	console.log('  VITE_SENTRY_DSN:', dsn ? `${dsn.substring(0, 20)}...` : 'undefined');
	console.log('  Current mode:', import.meta.env.MODE);
	console.log('  Should initialize:', !!(environment && dsn && environment !== 'local'));

	// Skip Sentry in local development or if DSN is not configured
	return !!(environment && dsn && environment !== 'local');
}

/**
 * Creates Sentry configuration with environment-specific settings
 *
 * @returns {SentryConfig} Sentry configuration object
 */
function createSentryConfig(): SentryConfig {
	const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development';

	return {
		dsn: import.meta.env.VITE_SENTRY_DSN,
		environment,
		// Lower sample rates for development, higher for production
		tracesSampleRate: environment === 'production' ? 0.1 : 0.5,
		replaysSessionSampleRate: environment === 'production' ? 0.01 : 0.1,
		replaysOnErrorSampleRate: 1.0,
		integrations: [
			Sentry.browserTracingIntegration(),
			Sentry.replayIntegration({
				maskAllText: environment === 'production',
				blockAllMedia: environment === 'production',
			}),
		],
	};
}

/**
 * Initializes Sentry with React-specific integrations
 *
 * Sets up comprehensive error tracking, performance monitoring, and session replay
 * for non-local environments. Includes React Router integration for navigation tracking.
 */
export function initializeSentry(): void {
	// Run debug diagnostics in development only
	if (import.meta.env.DEV) {
		initializeSentryWithDebug();
	}

	if (!shouldInitializeSentry()) {
		console.log('🔧 Sentry: Skipped initialization (local development)');
		if (import.meta.env.DEV) {
			logSentryDebugInfo();
		}
		return;
	}

	const config = createSentryConfig();

	try {
		Sentry.init({
			...config,
			beforeSend(event, hint) {
				// Filter out development-specific errors
				if (event.exception) {
					const error = hint.originalException;
					if (error instanceof Error) {
						// Skip common development errors
						if (error.message.includes('ResizeObserver loop limit exceeded')) {
							return null;
						}
						if (error.message.includes('Non-Error promise rejection captured')) {
							return null;
						}
					}
				}
				return event;
			},
			beforeSendTransaction(event) {
				// Skip transactions for static assets and health checks
				if (
					event.transaction?.includes('/assets/') ||
					event.transaction?.includes('/health')
				) {
					return null;
				}
				return event;
			},
		});

		console.log(`✅ Sentry: Successfully initialized for ${config.environment} environment`);

		// Log debug info after successful initialization (development only)
		if (import.meta.env.DEV) {
			setTimeout(() => logSentryDebugInfo(), 1000);
		}
	} catch (error) {
		console.error('❌ Sentry: Failed to initialize:', error);
		if (import.meta.env.DEV) {
			logSentryDebugInfo();
		}
	}
}

/**
 * Creates a Sentry-wrapped React Router for navigation tracking
 *
 * @param routes - React Router route configuration
 * @returns {ReturnType<typeof createBrowserRouter>} Sentry-instrumented router
 */
export const createSentryRouter = (routes: Parameters<typeof createBrowserRouter>[0]) => {
	return createBrowserRouter(routes);
};

/**
 * Manually captures an exception to Sentry
 *
 * @param error - Error to capture
 * @param context - Additional context for the error
 */
export function captureException(error: Error, context?: Record<string, any>): void {
	if (!shouldInitializeSentry()) {
		console.error('Development Error:', error, context);
		return;
	}

	Sentry.withScope(scope => {
		if (context) {
			Object.entries(context).forEach(([key, value]) => {
				scope.setContext(key, value);
			});
		}
		Sentry.captureException(error);
	});
}

/**
 * Manually captures a message to Sentry
 *
 * @param message - Message to capture
 * @param level - Severity level
 * @param context - Additional context
 */
export function captureMessage(
	message: string,
	level: Sentry.SeverityLevel = 'info',
	context?: Record<string, any>
): void {
	if (!shouldInitializeSentry()) {
		console.log('Development Message:', message, context);
		return;
	}

	Sentry.withScope(scope => {
		if (context) {
			Object.entries(context).forEach(([key, value]) => {
				scope.setContext(key, value);
			});
		}
		Sentry.captureMessage(message, level);
	});
}

/**
 * Sets user context for Sentry
 *
 * @param user - User information
 */
export function setUser(user: { id: string; email?: string; [key: string]: any }): void {
	if (!shouldInitializeSentry()) {
		return;
	}

	Sentry.setUser(user);
}

/**
 * Clears user context from Sentry
 */
export function clearUser(): void {
	if (!shouldInitializeSentry()) {
		return;
	}

	Sentry.setUser(null);
}

/**
 * Creates a Sentry error boundary component
 */
export const SentryErrorBoundary = Sentry.ErrorBoundary;

/**
 * Export Sentry for direct usage when needed
 */
export { Sentry };
